import {Dispatch, SetStateAction, useCallback} from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'src/lib'
import { OperacionCorporateCuentaFields } from '../types/fields'
import { zodResolver } from '@hookform/resolvers/zod'
import { HttpCorporate } from '../api/httpCorporate'
import toast from 'react-hot-toast'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, InputLabel, Stack } from '@mui/material'
import { AsyncSelect, ControlledTextField } from 'src/components/forms'
import { HttpCacheOperacion } from 'src/pages/sistema/api/httpCacheOperacion'
import { OperacionCorporateImporteFields } from 'src/pages/sistema/types/importes'
import { HttpCacheGeneral } from 'src/http/httpCacheGeneral'
import { TextField } from 'src/lib/itaptec/components_forms/src'

const schemaNuevoDetalle = z.object({
    [OperacionCorporateCuentaFields.NroCuenta]: z.string(),
    [OperacionCorporateCuentaFields.RazonSocial]: z.string(),
    [OperacionCorporateCuentaFields.CodMoneda]: z.number(),
    [OperacionCorporateCuentaFields.ImporteConcertacion]: z.number(),
    [OperacionCorporateCuentaFields.ImporteDPV]: z.number(),
    [OperacionCorporateCuentaFields.ImporteFOP]: z.number()
})

type FormNuevoDetalle = z.infer<typeof schemaNuevoDetalle>

type DialogNuevoDetalleProps = {
    open: boolean
    setOpen: Dispatch<SetStateAction<boolean>>
    idOperacion: number
    idPais: number
    refetch: () => void
}

export const DialogNuevoDetalle = ({ open, idOperacion, setOpen, refetch }: DialogNuevoDetalleProps) => {
    const { control, handleSubmit, watch } = useForm<FormNuevoDetalle>({
        resolver: zodResolver(schemaNuevoDetalle),
        defaultValues: {
            [OperacionCorporateCuentaFields.CodMoneda]: 1
        }
    })

    const alGuardarDetalle = (data: FormNuevoDetalle) => {
        const promise = HttpCorporate.postCuenta(idOperacion, data)

        toast
            .promise(promise, {
                loading: 'Guardando',
                success: 'Guardado correctamente',
                error: 'Error al guardar'
            })
            .then(() => {
                refetch()
                setOpen(false)
            })
    }

    const monedaSeleccionada = watch(OperacionCorporateCuentaFields.CodMoneda)

    const getImportes = useCallback(() =>
        HttpCacheOperacion.getImportesCorporatePorMoneda(monedaSeleccionada)
      ,[monedaSeleccionada])

    return (
        <Dialog open={open} maxWidth='md' fullWidth>
            <DialogTitle>Nueva cuenta</DialogTitle>
            <DialogContent>
                <Stack spacing={4} pt={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <TextField
                                control={control}
                                name={OperacionCorporateCuentaFields.NroCuenta}
                                label='Nro Cuenta'
                                fullWidth
                                mask={{
                                    mask: Number
                                }}
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField
                                control={control}
                                name={OperacionCorporateCuentaFields.RazonSocial}
                                label='Razón Social'
                                fullWidth
                                mask={{
                                    mask: /^[A-Za-z]+[A-Za-z ]*$/
                                }}
                                size='small'
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <AsyncSelect
                                control={control}
                                name={OperacionCorporateCuentaFields.CodMoneda}
                                label='Moneda'
                                loadOptions={() => HttpCacheGeneral.getMonedas()}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                    <InputLabel>Límites</InputLabel>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <AsyncSelect
                                control={control}
                                name={OperacionCorporateCuentaFields.ImporteConcertacion}
                                label='Límite Concertación'
                                loadOptions={getImportes}
                                fullWidth
                                valueField={OperacionCorporateImporteFields.CorporateImporte}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <AsyncSelect
                                control={control}
                                name={OperacionCorporateCuentaFields.ImporteDPV}
                                label='Límite DVP'
                                loadOptions={getImportes}
                                fullWidth
                                valueField={OperacionCorporateImporteFields.CorporateImporte}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <AsyncSelect
                                control={control}
                                name={OperacionCorporateCuentaFields.ImporteFOP}
                                label='Límite FOP'
                                loadOptions={getImportes}
                                fullWidth
                                valueField={OperacionCorporateImporteFields.CorporateImporte}
                            />
                        </Grid>
                    </Grid>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant='text' color='primary' onClick={() => setOpen(false)}>
                    Cancelar
                </Button>
                <Button variant='contained' color='primary' onClick={handleSubmit(alGuardarDetalle)}>
                    Guardar
                </Button>
            </DialogActions>
        </Dialog>
    )
}
